import React, { useState } from 'react';
import { Box, Collapse, Tooltip } from '@mui/material';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ThemeColors } from '../../context/ThemeContext';

interface CollapsibleFiltersProps {
  theme: ThemeColors;
  /** number of filters currently active — shows a badge and the Clear pill */
  activeCount?: number;
  onClear?: () => void;
  /** localStorage key persisting the open state across visits */
  storageKey?: string;
  /** always-visible controls on the chip row (right side), e.g. an Add button */
  actions?: React.ReactNode;
  /** the filter controls, hidden until expanded */
  children: React.ReactNode;
}

/**
 * Finance-style collapsed filters: a compact "Filters" funnel pill with an
 * active-count badge and a Clear pill; the actual controls live in a
 * Collapse underneath. Extracted from ExpenseManagement for reuse so every
 * module's filter row looks and behaves the same.
 */
const CollapsibleFilters: React.FC<CollapsibleFiltersProps> = ({
  theme, activeCount = 0, onClear, storageKey, actions, children,
}) => {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return false;
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (storageKey) {
        try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
      }
      return next;
    });
  };

  const highlighted = open || activeCount > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip title={open ? 'Hide filters' : 'Show filters'}>
          <button
            onClick={toggle}
            data-testid="filter-toggle-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 16,
              border: highlighted ? `1.5px solid ${theme.primary || 'var(--action)'}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
              backgroundColor: highlighted ? `${theme.primary || 'var(--action)'}20` : theme.gray?.[100] || '#F0F0F0',
              color: highlighted ? theme.primary || 'var(--action)' : theme.gray?.[500] || '#666',
              fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit',
              minHeight: 32, whiteSpace: 'nowrap',
            }}
          >
            <FunnelIcon style={{ width: 14, height: 14 }} />
            Filters
            {activeCount > 0 && (
              <span style={{
                backgroundColor: theme.primary || 'var(--action)',
                color: 'var(--text-inverse)',
                borderRadius: 10,
                padding: '0 6px',
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 16,
                textAlign: 'center',
              }}>{activeCount}</span>
            )}
          </button>
        </Tooltip>
        {activeCount > 0 && onClear && (
          <button
            onClick={onClear}
            data-testid="filter-clear-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 16,
              border: `1px solid ${theme.danger || '#D99982'}50`,
              backgroundColor: `${theme.danger || '#D99982'}15`,
              color: theme.danger || '#D99982',
              fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit',
              minHeight: 32, whiteSpace: 'nowrap',
            }}
          >
            <XMarkIcon style={{ width: 14, height: 14 }} />
            Clear
          </button>
        )}
        {actions && <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>{actions}</Box>}
      </Box>
      <Collapse in={open} timeout="auto">
        <Box sx={{ pt: 1.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export default CollapsibleFilters;
